import * as React from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
  colors,
} from "@mui/material";

import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import { DesktopDatePicker, DesktopTimePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import SelectSimple from "../../components/SelectSimple";
import SelectTimezone from "../../components/SelectTimezone";
import { Shift, ShiftTask } from "../../types/shift";
import ModalShiftTaskForm from "./ModalShiftTaskForm";
import Add from "@mui/icons-material/Add";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import { Project } from "../../types/project";
import {
  getNextNearestMinuteInterval,
  mergeDateAndTime,
} from "../../utils/dateHelper";
import {
  useAddProjectShiftMutation,
  useEditProjectShiftMutation,
} from "../../services/projectShift.service";
import { toast } from "react-toastify";

interface ModalShiftFormProps {
  open: boolean;
  onClose: () => void;
  data: Shift | null;
  project?: Project;
  onAdd?: (shift: Shift) => void;
}

const repeatOptions = [
  { value: "never", label: "Never" },
  { value: "this week", label: "This Week" },
  { value: "every week", label: "Weekly" },
  { value: "every 2 week", label: "Every 2 Week" },
  { value: "every 3 week", label: "Every 3 Week" },
  { value: "every 4 week", label: "Every 4 Week" },
  { value: "every 5 week", label: "Every 5 Week" },
];

const dayOptions = [
  { value: "is_monday", label: "Mon" },
  { value: "is_tuesday", label: "Tue" },
  { value: "is_wednesday", label: "Wed" },
  { value: "is_thursday", label: "Thu" },
  { value: "is_friday", label: "Fri" },
  { value: "is_saturday", label: "Sat" },
  { value: "is_sunday", label: "Sun" },
];

const ModalShiftForm = ({
  open,
  onClose,
  data,
  project,
  onAdd,
}: ModalShiftFormProps) => {
  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
    watch,
    setError,
  } = useForm();

  const [tasks, setTasks] = React.useState<Array<ShiftTask>>([]);
  const [taskModal, setTaskModal] = React.useState<{
    open: boolean;
    data: ShiftTask | null;
  }>({
    open: false,
    data: null,
  });
  const [add, { isLoading: adding }] = useAddProjectShiftMutation();
  const [edit, { isLoading: updating }] = useEditProjectShiftMutation();

  const handleAddTask = (task: ShiftTask) => {
    task.id = Date.now();
    setTaskModal({ open: false, data: null });
    setTasks([...tasks, task]);
  };
  const handleDeleteTask = (task: ShiftTask) => {
    const newTasks = tasks.filter((it) => it.id != task.id);
    setTasks(newTasks);
  };

  const onSubmit = async (payload: any) => {
    try {
      payload.project_id = project?.id;
      payload.start_date = dayjs(
        mergeDateAndTime(payload.date1, payload.time1)
      ).format("YYYY-MM-DD HH:mm");
      payload.end_date = dayjs(
        mergeDateAndTime(payload.date2, payload.time2)
      ).format("YYYY-MM-DD HH:mm");
      console.log("onSubmit", payload);

      delete payload.date1;
      delete payload.date2;
      delete payload.time1;
      delete payload.time2;
      if (!data) {
        const shift: Shift = await add(payload).unwrap();
        toast.success("Shift created");
      } else {
        payload.id = data.id;
        await edit(payload).unwrap();
        toast.success("Template updated");
      }
      if (onAdd) onAdd(payload);
    } catch (err: any) {
      toast.error(err);
    }
  };

  React.useEffect(() => {
    if (open) {
      const nextMin = getNextNearestMinuteInterval(5);
      setValue("name", data?.name);
      setValue("description", data?.description);
      setValue("time1", data?.start_date || nextMin);
      setValue("time2", data?.end_date || nextMin);
      setValue("repeat", data?.repeat || "never");
      setValue("is_monday", data?.is_monday || false);
      setValue("is_tuesday", data?.is_tuesday || false);
      setValue("is_wednesday", data?.is_wednesday || false);
      setValue("is_thursday", data?.is_thursday || false);
      setValue("is_friday", data?.is_friday || false);
      setValue("is_saturday", data?.is_saturday || false);
      setValue("is_sunday", data?.is_sunday || false);
      setValue("timezone", project?.timezone);
      setValue("date1", project?.start_date);
      setValue("date2", data?.end_date);
      setTasks([]);
      clearErrors();
    }
  }, [data, project, open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth={true} maxWidth="sm">
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle id="alert-dialog-title">
          {!data ? "Create" : "Edit"} Shift
        </DialogTitle>
        <DialogContent>
          <SelectTimezone
            onChange={() => {}}
            value={project?.timezone || ""}
            disabled={true}
            label="Job's Timezone"
          />
          <Controller
            name="start_date"
            control={control}
            rules={{ required: false }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <DesktopDatePicker
                sx={{ width: "100%" }}
                onChange={onChange}
                value={dayjs(value)}
                format="ddd, MMM D, YYYY"
                label="Start Date"
              />
            )}
          />
          <Stack direction={"row"}>
            <Controller
              name="time1"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <DesktopTimePicker
                  sx={{ mr: 1, width: "100%" }}
                  onChange={onChange}
                  value={!!value ? dayjs(value) : null}
                  format="hh:mm A"
                  minutesStep={5}
                  label="Start Shift"
                  slotProps={{
                    textField: {
                      helperText: !!errors.time1 && (
                        <Typography variant="caption" color={"error"}>
                          Please set a valid value
                        </Typography>
                      ),
                    },
                  }}
                />
              )}
            />
            <Controller
              name="time2"
              control={control}
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <DesktopTimePicker
                  sx={{ width: "100%" }}
                  onChange={onChange}
                  value={!!value ? dayjs(value) : null}
                  format="hh:mm A"
                  minutesStep={5}
                  label="End Shift"
                  slotProps={{
                    textField: {
                      helperText: !!errors.time2 && (
                        <Typography variant="caption" color={"error"}>
                          Please set a valid value
                        </Typography>
                      ),
                    },
                  }}
                />
              )}
            />
          </Stack>
          <Divider sx={{ mt: 2, mb: 1 }} />
          <Controller
            name="repeat"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, value, ref } }) => (
              <SelectSimple
                title="Repeat"
                data={repeatOptions}
                value={value}
                sx={{ mt: 1 }}
                onChange={onChange}
              />
            )}
          />
          {watch("repeat") != "never" && (
            <>
              <Stack direction={"row"} sx={{ mt: 1 }}>
                {dayOptions.map((option: any) => {
                  return (
                    <Paper
                      elevation={0}
                      variant="outlined"
                      sx={{ mr: 1 }}
                      key={option.value}
                    >
                      <Controller
                        name={option.value}
                        control={control}
                        render={({ field }) => {
                          return (
                            <FormControlLabel
                              control={<Checkbox {...field} />}
                              label={option.label}
                              labelPlacement="bottom"
                              sx={{ width: 30 }}
                              slotProps={{ typography: { fontSize: 12 } }}
                            />
                          );
                        }}
                      />
                    </Paper>
                  );
                })}
              </Stack>
              <Stack direction={"row"}>
                <Controller
                  name="date1"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DesktopDatePicker
                      sx={{ width: "100%", mr: 1 }}
                      onChange={onChange}
                      value={!!value ? dayjs(value) : null}
                      disabled={true}
                      format="ddd, MMM D, YYYY"
                      label="Start Date"
                    />
                  )}
                />
                <Controller
                  name="date2"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { onChange, onBlur, value, ref } }) => (
                    <DesktopDatePicker
                      sx={{ width: "100%" }}
                      onChange={onChange}
                      value={!!value ? dayjs(value) : null}
                      format="ddd, MMM D, YYYY"
                      label="End Date"
                    />
                  )}
                />
              </Stack>
            </>
          )}
          <Divider sx={{ mt: 1, mb: 2 }} />
          <Controller
            name="name"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <TextField
                label="Shift Name"
                value={value || ""}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.name}
                helperText={!!errors.name && "This field is required"}
              />
            )}
          />
          <Controller
            name="confidential_information"
            control={control}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <TextField
                label="Confidential Information"
                placeholder="Optional"
                value={value || ""}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
              />
            )}
          />
          <Controller
            name="num_of_talent"
            control={control}
            rules={{ required: true }}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <TextField
                label="Number of talent required"
                value={value || ""}
                fullWidth
                onChange={onChange}
                onBlur={onBlur}
                error={!!errors.name}
                helperText={!!errors.name && "This field is required"}
              />
            )}
          />
          <Button
            variant="outlined"
            sx={{ mt: 2 }}
            onClick={() => setTaskModal({ open: true, data: null })}
            startIcon={<Add />}
          >
            Add Shift Task
          </Button>
          {tasks.length > 0 && (
            <List>
              {tasks.map((it, idx) => (
                <ListItem
                  sx={{ backgroundColor: "#f5f5f5", px: 2 }}
                  divider
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleDeleteTask(it)}>
                      <DeleteOutline />
                    </IconButton>
                  }
                >
                  <ListItemText
                    key={idx}
                    primary={it.name}
                    secondary={it.description}
                    secondaryTypographyProps={{ fontSize: 11 }}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <LoadingButton type="submit" variant="contained" loading={false}>
            {!data ? "Create" : "Update"}
          </LoadingButton>
        </DialogActions>
        <ModalShiftTaskForm
          open={taskModal.open}
          onClose={() => setTaskModal({ open: false, data: null })}
          data={taskModal.data}
          onAdd={handleAddTask}
        />
      </form>
    </Dialog>
  );
};
export default ModalShiftForm;
